/**
 * Modular furniture assembly — IKEA-style kitchen cabinets.
 *
 * These items have no mesh of their own (their model XML says `Model="!!TEMP!!"`).
 * The geometry is a tree of parts carried in the project file:
 *
 *   cabinet/<size>            no mesh — the item itself
 *   ├─ cabinet-layout/<hXX-wYY>   no mesh — positions the fronts at the cabinet's face
 *   │  └─ handle-layout/<hl-…>    no mesh — positions each door and its handle
 *   │     └─ door/bodbyn/<id>     MESH
 *   └─ frame/<size>/<id>          MESH
 *
 * Each level carries its own `Position` (centimetres) and `Rotation` (degrees) in Unity
 * axes, and its own material overrides. Transforms compose parent-first; only the leaves
 * contribute geometry, so a level with no mesh still has to be walked through.
 */
import * as THREE from 'three';
import type { Item, Module, Surface } from '../../core/hp/types';

export interface PartPlacement {
  modelId: string;
  /** Composed transform in the item's own model space, metres. */
  matrix: THREE.Matrix4;
  /** Material overrides from this part's own `<Info><Materials>`, with the parents' as fallback. */
  materials: Record<string, Surface>;
  depth: number;
  /** The `<name>` of the first-level module this part belongs to: the layout slot the app names (a drawer, the frame). */
  slot: string;
}

/**
 * Flatten a module tree into placements, composing transforms parent-first.
 *
 * These offsets live in the PART's own authored space, not in plan space — the same
 * space the converted meshes are in — so no axis is flipped here. Negating Z instead
 * pushes every door a full frame-depth to the wrong side, which reads as a plausible
 * cabinet that is twice as deep as it should be.
 */
export function flattenModules(item: Item): PartPlacement[] {
  const out: PartPlacement[] = [];
  walk(item.modules ?? [], new THREE.Matrix4(), item.materials ?? {}, 0, '', out);
  return out;
}

function walk(
  list: Module[],
  parent: THREE.Matrix4,
  inherited: Record<string, Surface>,
  depth: number,
  slot: string,
  out: PartPlacement[],
): void {
  for (const m of list) {
    const local = transformOf(m);
    const world = new THREE.Matrix4().multiplyMatrices(parent, local);
    const materials = { ...inherited, ...(m.materials ?? {}) };
    const own = depth ? slot : m.name;
    if (m.modelRef.kind !== 'none' && m.modelRef.id) {
      out.push({ modelId: m.modelRef.id, matrix: world, materials, depth, slot: own });
    }
    if (m.modules?.length) walk(m.modules, world, materials, depth + 1, own, out);
  }
}

function transformOf(m: Module): THREE.Matrix4 {
  const [px, py, pz] = m.position ?? [0, 0, 0];
  const [rx, ry, rz] = m.rotation ?? [0, 0, 0];
  const d = Math.PI / 180;
  const e = new THREE.Euler(rx * d, ry * d, rz * d, 'YXZ');   // Unity's Euler order
  return new THREE.Matrix4()
    .makeRotationFromEuler(e)
    .setPosition(px / 100, py / 100, pz / 100);
}
